/**
 * Transform functions for FML benefits
 *
 * These functions handle the bi-directional transformation between:
 * - Frontend grid format (flat fields with separate type and amount)
 * - Backend API format (nested objects with type and amount)
 */

import { RfpFlmBenefitTypeType } from "@platform/openapi-types";

import {
  cleanupNotDisclosedBenefits,
  shouldIncludeBenefitInApi,
  transformTypeAmountFromApi,
  transformTypeAmountToApi,
} from "../shared/builders";

import type { FmlBenefitType, FmlBenefitsApi, FmlBenefitsGrid } from "./types";

/**
 * Validates a transformed type/amount benefit and returns it only if it meets
 * the required conditions.
 *
 * For types that require an amount (e.g., additionalDollarAmount), the benefit
 * is only included if the amount is greater than 0. For all other types, the
 * benefit is returned as-is.
 *
 * @param benefit - The transformed benefit from transformTypeAmountToApi
 * @param typesRequiringAmount - Type value(s) that require a positive amount
 * @returns The benefit if valid, or undefined if it should be excluded
 */
const validateTypeAmountBenefit = (
  benefit: FmlBenefitType | undefined,
  typesRequiringAmount: RfpFlmBenefitTypeType[],
): FmlBenefitType | undefined => {
  if (benefit && typesRequiringAmount.includes(benefit.type)) {
    return benefit.amount ? benefit : undefined;
  }

  return benefit;
};

/**
 * Transforms FML benefits from frontend grid format to API format
 *
 * Converts flat grid fields into nested API structures for:
 * - Federal FMLA (type, amount)
 * - Unpaid State Leaves (type, amount)
 * - Military USERRA (type, amount)
 * - Jury Duty (type, amount)
 * - ADA (type, amount)
 * - Integrated STD/FMLA Claim Intake (enum)
 * - History and Takeover (text)
 * - Company Leaves (text)
 * - Correspondence (text)
 *
 * @param benefits - The benefits object from the frontend grid (flat fields)
 * @returns Transformed benefits object matching the API structure (nested objects)
 */
export const transformFmlBenefitsToApi = (
  benefits: Partial<FmlBenefitsGrid>,
): FmlBenefitsApi => {
  const transformed: FmlBenefitsApi = { objectType: "fml" };

  // Transform Federal FMLA
  transformed.federalFmla = validateTypeAmountBenefit(
    transformTypeAmountToApi(
      benefits.federalFmlaType,
      benefits.federalFmlaAmount,
      [RfpFlmBenefitTypeType.additionalDollarAmount],
    ) as FmlBenefitType | undefined,
    [RfpFlmBenefitTypeType.additionalDollarAmount],
  );

  // Transform Unpaid State Leaves
  transformed.unpaidStateLeaves = validateTypeAmountBenefit(
    transformTypeAmountToApi(
      benefits.unpaidStateLeavesType,
      benefits.unpaidStateLeavesAmount,
      [RfpFlmBenefitTypeType.additionalDollarAmount],
    ) as FmlBenefitType | undefined,
    [RfpFlmBenefitTypeType.additionalDollarAmount],
  );

  // Transform Military (USERRA)
  transformed.militaryUserra = validateTypeAmountBenefit(
    transformTypeAmountToApi(
      benefits.militaryUserraType,
      benefits.militaryUserraAmount,
      [RfpFlmBenefitTypeType.additionalDollarAmount],
    ) as FmlBenefitType | undefined,
    [RfpFlmBenefitTypeType.additionalDollarAmount],
  );

  // Transform Jury Duty
  transformed.juryDuty = validateTypeAmountBenefit(
    transformTypeAmountToApi(benefits.juryDutyType, benefits.juryDutyAmount, [
      RfpFlmBenefitTypeType.additionalDollarAmount,
    ]) as FmlBenefitType | undefined,
    [RfpFlmBenefitTypeType.additionalDollarAmount],
  );

  // Transform ADA
  transformed.ada = validateTypeAmountBenefit(
    transformTypeAmountToApi(benefits.adaType, benefits.adaAmount, [
      RfpFlmBenefitTypeType.additionalDollarAmount,
    ]) as FmlBenefitType | undefined,
    [RfpFlmBenefitTypeType.additionalDollarAmount],
  );

  // Copy simple fields directly
  if (shouldIncludeBenefitInApi(benefits.integratedStdFmlaClaimIntake)) {
    transformed.integratedStdFmlaClaimIntake =
      benefits.integratedStdFmlaClaimIntake;
  }

  if (benefits.historyAndTakeover !== undefined) {
    transformed.historyAndTakeover = benefits.historyAndTakeover;
  }

  if (benefits.companyLeaves !== undefined) {
    transformed.companyLeaves = benefits.companyLeaves;
  }

  if (benefits.correspondence !== undefined) {
    transformed.correspondence = benefits.correspondence;
  }

  return cleanupNotDisclosedBenefits(transformed) as FmlBenefitsApi;
};

/**
 * Transforms FML benefits from API format to frontend grid format
 *
 * Converts nested API objects into flat grid fields for:
 * - Federal FMLA (type, amount)
 * - Unpaid State Leaves (type, amount)
 * - Military USERRA (type, amount)
 * - Jury Duty (type, amount)
 * - ADA (type, amount)
 * - Integrated STD/FMLA Claim Intake (enum)
 * - History and Takeover (text)
 * - Company Leaves (text)
 * - Correspondence (text)
 *
 * @param benefits - The benefits object from the API (nested objects)
 * @returns Transformed benefits object in grid format (flat fields)
 */
export const transformFmlBenefitsFromApi = (
  benefits: Partial<FmlBenefitsApi>,
): Partial<FmlBenefitsGrid> => {
  const transformed: Partial<FmlBenefitsGrid> = { objectType: "fml" };

  // Transform Federal FMLA - using shared builder
  Object.assign(
    transformed,
    transformTypeAmountFromApi(benefits.federalFmla, "federalFmla"),
  );

  // Transform Unpaid State Leaves - using shared builder
  Object.assign(
    transformed,
    transformTypeAmountFromApi(benefits.unpaidStateLeaves, "unpaidStateLeaves"),
  );

  // Transform Military (USERRA) - using shared builder
  Object.assign(
    transformed,
    transformTypeAmountFromApi(benefits.militaryUserra, "militaryUserra"),
  );

  // Transform Jury Duty - using shared builder
  Object.assign(
    transformed,
    transformTypeAmountFromApi(benefits.juryDuty, "juryDuty"),
  );

  // Transform ADA - using shared builder
  Object.assign(transformed, transformTypeAmountFromApi(benefits.ada, "ada"));

  // Copy simple fields directly
  if (benefits.integratedStdFmlaClaimIntake !== undefined) {
    transformed.integratedStdFmlaClaimIntake =
      benefits.integratedStdFmlaClaimIntake;
  }

  if (benefits.historyAndTakeover !== undefined) {
    transformed.historyAndTakeover = benefits.historyAndTakeover;
  }

  if (benefits.companyLeaves !== undefined) {
    transformed.companyLeaves = benefits.companyLeaves;
  }

  if (benefits.correspondence !== undefined) {
    transformed.correspondence = benefits.correspondence;
  }

  return transformed;
};
